import { selectRow, selectRowByRowIndex } from '@client-shared/ui-agGrid';

import {
  FETCH_CURRENCY_CONVERSION_RATES,
  FETCH_CURRENCY_CONVERSION_RATES_SUCCESS,
  FETCH_CURRENCY_CONVERSION_RATES_FAILURE,
  SAVE_CURRENCY_CONVERSION_RATE,
  SAVE_CURRENCY_CONVERSION_RATE_SUCCESS,
  SAVE_CURRENCY_CONVERSION_RATE_FAILURE,
  UPLOADING_CURRENCY_CONVERSION_RATES,
  UPLOADING_CURRENCY_CONVERSION_RATES_SUCCESS,
  UPLOADING_CURRENCY_CONVERSION_RATES_FAILURE,
  IMPORT_CURRENCY_CONVERSION_RATES,
  IMPORT_CURRENCY_CONVERSION_RATES_SUCCESS,
  IMPORT_CURRENCY_CONVERSION_RATES_FAILURE,
  CALCULATE_MISSING_RATES,
  IMPORT_CURRENCY_CONVERSION_RATES_PROGRESS,
  REMOVE_CURRENCY_CONVERSION_RATE,
  REMOVE_CURRENCY_CONVERSION_RATE_SUCCESS,
  REMOVE_CURRENCY_CONVERSION_RATE_FAILURE,
  CLEAR_UPLOADED_RATES,
} from './types';
import {
  fetchCurrencyConversionRates as fetchCurrencyConversionRatesApi,
  addCurrencyConversionRate as addCurrencyConversionRateApi,
  updateCurrencyConversionRate as updateCurrencyConversionRateApi,
  validateCurrencyConversionRates as validateCurrencyConversionRatesApi,
  importCurrencyConversionRates as importCurrencyConversionRatesApi,
  removeCurrencyConversionRate as removeCurrencyConversionRateApi,
  fetchCurrencyCodes,
} from '../api';
import { fetchCompanyCodes } from '../../companyCodesPage/api';
import { fetchConfigurationCurrencies } from '../../../reports/configurationSettings/api';
import { fetchingError, showAlert } from '../../../shared/store/actions';
import alertTypes from '../../../shared/alertTypes';
import { errorNotification, successNotification } from '../../../shared/store/notificationActions';
import { ADD_OR_UPDATE_VALUES } from '../../shared/constants';

import readXlsxFileAsJson from '../../../shared/xlsxImport/readXlsxFileAsJson';

import ModalNames from '../modalNames';
import getModalActions from '../../../shared/modals/getModalActions';

import agGridConfig from '../../../agGridConfig';

import { currencyConversionRatesSelector } from './selectors';

import { FIELD_MAPPINGS, MIN_COLUMNS_NUMBER, MAX_COLUMNS_NUMBER } from '../xlsxImport/constants';
import columnHeaders from '../xlsxImport/columnHeaders';
import fixDataTypesProcessor from '../xlsxImport/processors/fixDataTypes.processor';
import excelFileTypeValidator from '../../../shared/xlsxImport/validators/excelFileType.validator';
import columnHeadersValidator from '../../../shared/xlsxImport/validators/columnHeaders.validator';
import currentPeriodValidator from '../../../shared/xlsxImport/validators/currentPeriod.validator';
import noEmptyRowsValidator from '../../../shared/xlsxImport/validators/noEmptyRows.validator';
import emptyRowsTrimmer from '../../../shared/xlsxImport/processors/emptyRowsTrimmer.processor';
import calculateImportRangeFactory from '../../../shared/xlsxImport/calculateImportRangeFactory';

import { IMPORT_CHUNK_SIZE } from '../constants';
import promiseAllChunked from '../../../shared/chunkExport';

// Generates A->A, A->USD, USD->A unique combinations for fromCurrency and toCurrency
export function createCurrencyCombinations(rates) {
  const combinations = [];
  const alreadyCalculatedCurrencies = new Map();
  rates.forEach(({ fromCurrency, toCurrency }) => {
    if (!alreadyCalculatedCurrencies.has(fromCurrency)) {
      const fromCurrencyToFromCurrency = { fromCurrency, toCurrency: fromCurrency };
      combinations.push(fromCurrencyToFromCurrency);
      const fromCurrencyToUSD = { fromCurrency, toCurrency: 'USD' };
      combinations.push(fromCurrencyToUSD);
      const fromUSDToFromCurrency = { fromCurrency: 'USD', toCurrency: fromCurrency };
      combinations.push(fromUSDToFromCurrency);
      alreadyCalculatedCurrencies.set(fromCurrency, true);
    }

    if (!alreadyCalculatedCurrencies.has(toCurrency)) {
      const toCurrencyToToCurrency = { fromCurrency: toCurrency, toCurrency };
      combinations.push(toCurrencyToToCurrency);
      const toCurrencyToUSD = { fromCurrency: toCurrency, toCurrency: 'USD' };
      combinations.push(toCurrencyToUSD);
      const USDToToCurrency = { fromCurrency: 'USD', toCurrency };
      combinations.push(USDToToCurrency);
      alreadyCalculatedCurrencies.set(toCurrency, true);
    }
  });
  return combinations;
}

function calculateRateForCombinatioon(combination, reversedCombination, periodName) {
  if (combination.fromCurrency === combination.toCurrency) {
    return {
      periodName,
      ...combination,
      openSpotRate: 1,
      endSpotRate: 1,
      monthlyOperatingRate: 1,
      weightedAverageRate: 1,
      fromCurrencyPrecision: 1,
      toCurrencyPrecision: 1,
    };
  }

  return {
    periodName,
    ...combination,
    fromCurrencyPrecision: reversedCombination.fromCurrencyPrecision,
    toCurrencyPrecision: reversedCombination.toCurrencyPrecision,
    openSpotRate: 1/reversedCombination.openSpotRate,
    endSpotRate: 1/reversedCombination.endSpotRate,
    monthlyOperatingRate: 1/reversedCombination.monthlyOperatingRate,
    weightedAverageRate: 1/reversedCombination.weightedAverageRate,
  };
}

const {
  showAction: showCurrencyConversionRateModalForAddEdit,
  hideAction: hideCurrencyConversionRateModalForAddEdit,
} = getModalActions(ModalNames.CurrencyConversionRateAddEdit);

const {
  showAction: showCurrencyConversionRateModalForRemove,
  hideAction: hideCurrencyConversionRateModalForRemove,
} = getModalActions(ModalNames.CurrencyConversionRateRemove);

export {
  showCurrencyConversionRateModalForAddEdit,
  hideCurrencyConversionRateModalForAddEdit,
  showCurrencyConversionRateModalForRemove,
  hideCurrencyConversionRateModalForRemove,
};

export const fetchCurrencyConversionRates = period => async dispatch => {
  dispatch({
    type: FETCH_CURRENCY_CONVERSION_RATES,
  });

  try {
    const currencyCodesReponse = await fetchCurrencyCodes();

    const response = await fetchCurrencyConversionRatesApi(period.name);

    return dispatch({
      type: FETCH_CURRENCY_CONVERSION_RATES_SUCCESS,
      currencyConversionRates: response,
      currencyOptions: currencyCodesReponse.map(({ currencyCode }) => ({ label: currencyCode, value: currencyCode })),
    });
  } catch (error) {
    dispatch({
      type: FETCH_CURRENCY_CONVERSION_RATES_FAILURE,
    });

    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      return;
    }
    throw error;
  }
};

export const saveCurrencyConversionRate = (currencyConversionRate, openForEdit, { callback }) => async dispatch => {
  dispatch({
    type: SAVE_CURRENCY_CONVERSION_RATE,
  });

  try {
    if (openForEdit) {
      await updateCurrencyConversionRateApi(currencyConversionRate);
      dispatch(successNotification('Currency Conversion Rate added'));
    } else {
      await addCurrencyConversionRateApi(currencyConversionRate);
      dispatch(successNotification('Currency Conversion Rate updated'));
    }
    dispatch(hideCurrencyConversionRateModalForAddEdit());
    dispatch(selectRow({
      name: agGridConfig.adminCurrencyConversionRates.name,
      fields: agGridConfig.adminCurrencyConversionRates.fields,
      data: currencyConversionRate,
    }));
    dispatch({
      type: SAVE_CURRENCY_CONVERSION_RATE_SUCCESS,
    });


    dispatch(fetchCurrencyConversionRates({ name: currencyConversionRate.periodName }));
    if (callback) {
      return callback();
    }
  } catch (error) {
    dispatch({
      type: SAVE_CURRENCY_CONVERSION_RATE_FAILURE,
    });

    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      return;
    }
    throw error;
  }
};

export const removeCurrencyConversionRate = (currencyConversionRate, { rowIndex }) => async dispatch => {
  dispatch({
    type: REMOVE_CURRENCY_CONVERSION_RATE,
  });

  try {
    await removeCurrencyConversionRateApi(currencyConversionRate);
    dispatch({
      type: REMOVE_CURRENCY_CONVERSION_RATE_SUCCESS,
    });
    dispatch(successNotification('Currency Conversion Rate removed'));
    dispatch(hideCurrencyConversionRateModalForRemove());
    dispatch(selectRowByRowIndex(rowIndex ? rowIndex - 1 : 0));

    return dispatch(fetchCurrencyConversionRates({ name: currencyConversionRate.periodName }));
  } catch (error) {
    dispatch({
      type: REMOVE_CURRENCY_CONVERSION_RATE_FAILURE,
    });

    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      return;
    }

    throw error;
  }
};

export const uploadCurrencyConversionRatesFromFile = ({ file, currentPeriod }) => async (dispatch, getState) => {
  dispatch({
    type: UPLOADING_CURRENCY_CONVERSION_RATES,
  });

  try {
    const uploadedCurrencyConversionRates = await readXlsxFileAsJson({
      file,
      fileValidators: [excelFileTypeValidator],
      prevalidators: [columnHeadersValidator],
      postvalidators: [noEmptyRowsValidator, currentPeriodValidator],
      fieldMappings: FIELD_MAPPINGS,
      sheetName: 'Currency Conversion Rates',
      getRange: calculateImportRangeFactory('I'),
      preprocessors: [emptyRowsTrimmer, fixDataTypesProcessor],
      mapToJsonOptions: {
        header: columnHeaders,
      },
      validationData: {
        columnHeaders,
        currentPeriod,
        minColumnsNumber: MIN_COLUMNS_NUMBER,
        maxColumnsNumber: MAX_COLUMNS_NUMBER,
      },
    });
    const existingCurrencyConversionRates = currencyConversionRatesSelector(getState());
    const {
      importStats,
      extendedUploadedCurrencyConversionRates,
    } = await validateCurrencyConversionRatesApi({
      uploadedCurrencyConversionRates,
      fetchExistingEntitiesParams: {
        periodName: currentPeriod.name,
      },
    });

    if (importStats.errors === 0) {
      const companyCodes = await fetchCompanyCodes(currentPeriod.name);
      const currencies = await fetchConfigurationCurrencies();

      const hasValidData = companyCodes.every(({ associatedLeCountryCode }) => associatedLeCountryCode
        && currencies.find(currency => currency.countryCode === associatedLeCountryCode
        ));

      if (hasValidData) {
        const associatedLegalEntityCountryCodes = Array.from(
          new Set(companyCodes.map(
            companyCode => companyCode.associatedLeCountryCode
          ))
        );

        const associatedCurrencies = associatedLegalEntityCountryCodes.map((countryCode, index) => {
          const overrideTaxCurrency = companyCodes[index].overrideTaxCurrency;
          if (overrideTaxCurrency) {
            return overrideTaxCurrency;
          }
          return currencies.find(currency => currency.countryCode === countryCode).currencyCode;
        });


        // Combine uploaded Currency Conversion Rates and associated Legal Entity Country Codes
        const currencyConversionRates = [];
        associatedCurrencies.forEach(associatedCurrency => {
          uploadedCurrencyConversionRates.forEach(uploadedRate => {
            currencyConversionRates.push(
              {
                fromCurrency: uploadedRate.fromCurrency,
                toCurrency: associatedCurrency,
              },
              {
                fromCurrency: associatedCurrency,
                toCurrency: uploadedRate.fromCurrency,
              }
            );
          });
        });

        const currencyConversionRatesMap = new Map(existingCurrencyConversionRates.map(currency => {
          const mapKey = `${currency.fromCurrency}-${currency.toCurrency}`;
          return [mapKey, currency];
        }));

        const currencyCombinations = createCurrencyCombinations(currencyConversionRates);

        const missingRates = currencyCombinations
          .filter(combination =>
            !currencyConversionRatesMap.has(`${combination.fromCurrency}-${combination.toCurrency}`)
            && (currencyConversionRatesMap.has(`${combination.toCurrency}-${combination.fromCurrency}`) || combination.toCurrency === combination.fromCurrency))
          .map(combination => calculateRateForCombinatioon(
            combination,
            currencyConversionRatesMap.get(`${combination.toCurrency}-${combination.fromCurrency}`),
            currentPeriod.name
          ));

        dispatch({
          type: CALCULATE_MISSING_RATES,
          missingRates,
        });
      }
    }

    dispatch({
      type: UPLOADING_CURRENCY_CONVERSION_RATES_SUCCESS,
      extendedUploadedCurrencyConversionRates,
      importStats,
    });
  } catch (error) {
    if (error.isValidationError) {
      dispatch(showAlert(error.message, alertTypes.DANGER));
    } else {
      dispatch(errorNotification(error.message));
    }
    dispatch({
      type: UPLOADING_CURRENCY_CONVERSION_RATES_FAILURE,
    });
  }
};

export const setImportProgress = progress => ({
  type: IMPORT_CURRENCY_CONVERSION_RATES_PROGRESS,
  progress,
});

export const clearUploadedRates = () =>
  ({ type: CLEAR_UPLOADED_RATES });

export const importCurrencyConversionRates = currencyConversionRates => async dispatch => {
  dispatch({ type: IMPORT_CURRENCY_CONVERSION_RATES });

  const filteredEntities = currencyConversionRates
    .filter(({ addOrUpdate }) => addOrUpdate !== ADD_OR_UPDATE_VALUES.NO_CHANGE);

  try {
    await promiseAllChunked(
      filteredEntities,
      IMPORT_CHUNK_SIZE,
      importCurrencyConversionRatesApi,
      progress => dispatch(setImportProgress(progress))
    );

    const addedEntities = filteredEntities.filter(entity => entity.addOrUpdate === ADD_OR_UPDATE_VALUES.ADD);
    const updatedEntities = filteredEntities.filter(entity => entity.addOrUpdate === ADD_OR_UPDATE_VALUES.UPDATE);

    dispatch({
      type: IMPORT_CURRENCY_CONVERSION_RATES_SUCCESS,
      addedEntities,
      updatedEntities,
    });
    dispatch(setImportProgress(0));
  } catch (error) {
    dispatch({ type: IMPORT_CURRENCY_CONVERSION_RATES_FAILURE });
    dispatch(showAlert(error.message, alertTypes.DANGER));
    dispatch(setImportProgress(0));
  }
};


